import { Link } from "@inertiajs/react";
import { CirclePlusIcon, EllipsisIcon } from "lucide-react";
import { Button } from "@/components/ui/button";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import { FinancialAccount, PropsWithUser } from "@/types";
import { PageTitle } from "@/components/page-title";
import {
  AlertDialog,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
  AlertDialogTrigger,
} from "@/components/ui/alert-dialog";
import { TableCellLink } from "@/components/table-cell-link";

type Props = {
  financial_accounts: FinancialAccount[];
};

function FinancialAccountIndex({ financial_accounts }: PropsWithUser<Props>) {
  return (
    <>
      <div className="flex items-center justify-between gap-2">
        <PageTitle>Financial accounts</PageTitle>
        <Button asChild>
          <Link href="/financial_accounts/new">
            <CirclePlusIcon />
            Add financial account
          </Link>
        </Button>
      </div>

      <div className="mt-4 border rounded-md overflow-hidden">
        <Table>
          <TableHeader>
            <TableRow>
              <TableHead>Name</TableHead>
              <TableHead className="w-10">
                <span className="sr-only">Actions</span>
              </TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {financial_accounts.map((financial_account) => {
              const href = `/financial_accounts/${financial_account.id}`;

              return (
                <TableRow
                  key={financial_account.id}
                  className="has-[[data-row-link]:focus-visible]:outline-2 has-[[data-row-link]:focus-visible]:-outline-offset-2 has-[[data-row-link]:focus-visible]:outline-ring/50"
                >
                  <TableCell className="font-medium">
                    <TableCellLink href={href} />
                    {financial_account.name}
                  </TableCell>
                  <TableCell>
                    <FinancialAccountActions
                      financial_account={financial_account}
                    />
                  </TableCell>
                </TableRow>
              );
            })}
          </TableBody>
        </Table>
      </div>
    </>
  );
}

function FinancialAccountActions({
  financial_account,
}: {
  financial_account: FinancialAccount;
}) {
  return (
    <AlertDialog>
      <DropdownMenu>
        <DropdownMenuTrigger asChild>
          <Button variant="ghost" size="icon" className="size-8">
            <EllipsisIcon />
          </Button>
        </DropdownMenuTrigger>
        <DropdownMenuContent side="bottom" align="end">
          <AlertDialogTrigger asChild>
            <DropdownMenuItem>Delete</DropdownMenuItem>
          </AlertDialogTrigger>
        </DropdownMenuContent>
      </DropdownMenu>

      <AlertDialogContent>
        <AlertDialogHeader>
          <AlertDialogTitle>Delete financial account</AlertDialogTitle>
          <AlertDialogDescription>
            This action cannot be undone. This will permanently delete{" "}
            <span className="font-semibold">{financial_account.name}</span>{" "}
            financial account.
          </AlertDialogDescription>
        </AlertDialogHeader>
        <AlertDialogFooter>
          <AlertDialogCancel>Cancel</AlertDialogCancel>
          <Button variant="destructive" asChild>
            <Link
              href={`/financial_accounts/${financial_account.id}`}
              method="delete"
              preserveScroll
            >
              Delete
            </Link>
          </Button>
        </AlertDialogFooter>
      </AlertDialogContent>
    </AlertDialog>
  );
}

export default FinancialAccountIndex;
